import { useContext, useEffect } from 'react';
import { PricingTypes, WorkareaItemType, WorkareaItemTypes } from '../../common/constants';
import ItemTable, { ItemTableColumn } from './ItemTable';
import { ServerState, createViewItems, defaultViewItem } from './itemModel';
import { ItemsContext } from './ItemsRoot';
import VSpace from '../common/VSpace';
import WorkareaSummary from './WorkareaSummary';
import usePrevious from '../../hooks/usePrevious';
import { mutateNonMatchingRecurringItems, getRecurringWorkareaData, RecurringWorkareaMatches } from './recurringWorkareaModel';
import ToggleCatalogBrowserButton from '../ItemCatalog/ToggleCatalogBrowserButton';
import { hasValue } from '../../helpers/nullable';

export type Props = {
  workareaId: number
  type: WorkareaItemType
  quotedPrice?: number
  finalPrice?: number
  onDifferingWorkareas?: (data: RecurringWorkareaMatches) => void
}

const WorkareaItems = (props: Props) => {
  const itemsContext = useContext(ItemsContext)
  const { autoSave } = itemsContext

  const workareas = itemsContext.jobData.workareas ?? []
  const recurringData = getRecurringWorkareaData(props.workareaId, workareas)

  const workareaIds = recurringData.isRecurringWorkarea
    ? recurringData.allWorkareaIds
    : [props.workareaId]

  const estimatedItems = itemsContext.jobItemsQuery.data?.estimatedItems.filter(x => workareaIds.includes(x.workareaId)) ?? []
  const actualItems = itemsContext.jobItemsQuery.data?.actualItems.filter(x => workareaIds.includes(x.workareaId)) ?? []

  const workareaItems = props.type === WorkareaItemTypes.Estimated ? estimatedItems : actualItems
  const serverState: ServerState = {
    workareaItems,
    catalogItems: itemsContext.jobItemsQuery.data?.catalogItems ?? [],
    templateItems: itemsContext.jobItemsQuery.data?.templateItems ?? []
  }
  const workarea = workareas.find(x => x.id === props.workareaId)
  const previousPricingType = usePrevious(workarea?.pricingType)

  useEffect(() => {
    if (!autoSave && previousPricingType !== undefined && workarea !== undefined) {
      itemsContext.dispatch({
        type: 'PRICING_TYPE_UPDATED',
        workareaId: workarea.id,
        previousValue: previousPricingType,
        value: workarea.pricingType,
        serverState: {
          ...serverState,
          workareaItems: [
            ...(estimatedItems),
            ...(actualItems),
          ]
        }
      })
    }
    /* eslint-disable-next-line */
  }, [workarea?.pricingType, autoSave])

  if (itemsContext.focusedView !== 'workarea-estimated' && itemsContext.focusedView !== 'workarea-actual') {
    return <></>
  }

  if (!hasValue(workarea)) {
    return <></>
  }

  const columns: ItemTableColumn[] = [
    'name',
    'isBillable',
    'quantity',
    'costPlusTax',
    'breakeven',
    'totalBreakeven',
    'profit',
    'price',
    'totalPrice',
    'actions'
  ]
  if (props.type === WorkareaItemTypes.Actual) {
    columns.push('additional')
  }
  if (workarea?.pricingType === PricingTypes.TimeAndMaterials || workarea?.pricingType === PricingTypes.FlatRate) {
    columns.push('isTaxable')
  }

  const filter = {
    workareaItemType: props.type,
    workareaIds
  };
  const allViewItems = createViewItems(itemsContext.changeSet, serverState, itemsContext.jobData.visitData, itemsContext.jobData.budgets,
    itemsContext.jobData.jobIsSold, workareas, filter)

  if (recurringData.isRecurringWorkarea && props.type === WorkareaItemTypes.Estimated && !itemsContext.jobItemsQuery.isLoading) {
    const recurringWorkareaMatches = mutateNonMatchingRecurringItems(recurringData.masterWorkareaId, recurringData.childWorkareaIds, allViewItems)
    if (props.onDifferingWorkareas) {
      props.onDifferingWorkareas(recurringWorkareaMatches)
    }
  }

  const viewItems = allViewItems.filter(x => x.workareaId === props.workareaId)
  if (viewItems.length < 1) {
    viewItems.push({ ...defaultViewItem(), isEmptyRow: true })
  }

  const showSummary = !autoSave || props.type === WorkareaItemTypes.Estimated

  return <VSpace>
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '24px' }}>
      <ToggleCatalogBrowserButton />
      {showSummary &&
        <WorkareaSummary
          viewItems={viewItems}
          workareaItemType={props.type}
          quotedPrice={props.quotedPrice}
          finalPrice={props.finalPrice}
          workarea={workarea}
        />
      }
    </div>
    <ItemTable
      workareaItemType={props.type}
      budgets={itemsContext.jobData.budgets}
      workareas={workareas}
      viewItems={viewItems}
      serverState={serverState}
      isLoading={itemsContext.jobItemsQuery.isLoading}
      visibleColumns={columns}
      sortable={true}
    />
  </VSpace>
}

export default WorkareaItems
